import React, { useEffect, useMemo, useState } from "react";
import { Box } from "@mui/material";

const CONFETTI_COLORS = [
    "#f472b6",
    "#fb7185",
    "#a78bfa",
    "#60a5fa",
    "#34d399",
    "#fbbf24",
    "#f97316",
    "#2dd4bf",
];

function hashSeed(str) {
    let h = 0;
    for (let i = 0; i < str.length; i += 1) {
        h = (h * 31 + str.charCodeAt(i)) | 0;
    }
    return Math.abs(h);
}

function buildParticles(origins, burstId) {
    const particles = [];
    origins.forEach((origin, oi) => {
        const base = hashSeed(`${burstId}-${origin.key}`);
        for (let i = 0; i < 10; i += 1) {
            const angle = ((base + i * 37) % 360) * (Math.PI / 180);
            const dist = 28 + ((base + i * 17) % 42);
            const isStar = i % 3 === 0;
            particles.push({
                id: `${origin.key}-${i}`,
                xPct: origin.xPct,
                yPct: origin.yPct,
                dx: Math.cos(angle) * dist,
                dy: Math.sin(angle) * dist - 18,
                delay: oi * 40 + i * 18,
                size: isStar ? 10 + (i % 3) * 2 : 5 + (i % 4),
                color: isStar ? "#facc15" : CONFETTI_COLORS[(base + i) % CONFETTI_COLORS.length],
                isStar,
                rotate: ((base + i * 23) % 180) - 90,
            });
        }
    });
    return particles;
}

function BurstParticle({ particle, burstId }) {
    const [alive, setAlive] = useState(true);

    useEffect(() => {
        setAlive(true);
        const t = window.setTimeout(() => setAlive(false), particle.delay + 780);
        return () => window.clearTimeout(t);
    }, [particle.delay, particle.id, burstId]);

    if (!alive) return null;

    return (
        <Box
            aria-hidden
            sx={{
                position: "absolute",
                left: `${particle.xPct}%`,
                top: `${particle.yPct}%`,
                width: particle.size,
                height: particle.isStar ? particle.size : particle.size * 0.55,
                marginLeft: -particle.size / 2,
                marginTop: -(particle.isStar ? particle.size : particle.size * 0.55) / 2,
                borderRadius: particle.isStar ? "2px" : "2px",
                bgcolor: particle.isStar ? "transparent" : particle.color,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                pointerEvents: "none",
                zIndex: 20,
                "@keyframes wwBurstFly": {
                    "0%": {
                        opacity: 0,
                        transform: "translate(0, 0) rotate(0deg) scale(0.6)",
                    },
                    "15%": {
                        opacity: 1,
                    },
                    "100%": {
                        opacity: 0,
                        transform: `translate(${particle.dx}px, ${particle.dy + 18}px) rotate(${particle.rotate}deg) scale(0.6)`,
                    },
                },
                animation: `wwBurstFly 720ms cubic-bezier(0.22, 1, 0.36, 1) ${particle.delay}ms both`,
            }}
        >
            {particle.isStar ? (
                <Box
                    component="span"
                    sx={{
                        fontSize: particle.size,
                        fontWeight: 900,
                        lineHeight: 1,
                        color: particle.color,
                        textShadow: "0 0 6px rgba(250, 204, 21, 0.9)",
                    }}
                >
                    ★
                </Box>
            ) : null}
        </Box>
    );
}

/**
 * Confetti + star burst over revealed word cells (Android WordRevealBurst parity).
 * `origins` = [{ key, xPct, yPct }] in % of the grid box.
 */
export default function WordRevealBurst({ origins = [], burstId = 0 }) {
    const particles = useMemo(
        () => (origins.length ? buildParticles(origins, burstId) : []),
        [origins, burstId],
    );

    if (!particles.length) return null;

    return (
        <Box
            aria-hidden
            sx={{
                position: "absolute",
                inset: 0,
                pointerEvents: "none",
                overflow: "visible",
                zIndex: 20,
            }}
        >
            {particles.map((p) => (
                <BurstParticle key={`${burstId}-${p.id}`} particle={p} burstId={burstId} />
            ))}
        </Box>
    );
}
